import { useState, useEffect } from 'react'
import { doc, onSnapshot } from 'firebase/firestore'
import { db } from '../config/firebase'
import { useAuth } from './useAuth'

/**
 * Hook to manage user subscription state
 * Provides real-time updates from Firestore
 */
export function useSubscription() {
  const { user } = useAuth()
  const [subscription, setSubscription] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!user) {
      setSubscription(null)
      setLoading(false)
      return
    }

    setLoading(true)
    const monetizationRef = doc(db, 'users', user.uid, 'monetization', 'status')

    const unsubscribe = onSnapshot(
      monetizationRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data()
          setSubscription({
            plan: data.plan || 'free',
            planSource: data.planSource || null,
            stripeCustomerId: data.stripeCustomerId || null,
            subscriptionId: data.subscriptionId || null,
            subscriptionStatus: data.subscriptionStatus || null,
            subscriptionStartDate: data.subscriptionStartDate?.toDate() || null,
            subscriptionRenewalDate: data.subscriptionRenewalDate?.toDate() || null,
            // Override info
            overrideBy: data.overrideBy || null,
            overrideNote: data.overrideNote || null,
          })
        } else {
          // No monetization doc - user is on free plan
          setSubscription({
            plan: 'free',
            planSource: null,
            stripeCustomerId: null,
            subscriptionId: null,
            subscriptionStatus: null,
            subscriptionStartDate: null,
            subscriptionRenewalDate: null,
            overrideBy: null,
            overrideNote: null,
          })
        }
        setLoading(false)
        setError(null)
      },
      (err) => {
        console.error('Subscription listener error:', err)
        setError(err.message)
        setLoading(false)
      }
    )

    return () => unsubscribe()
  }, [user])

  // Helper methods
  const isSubscribed = subscription?.plan !== 'free'
  const isPro = subscription?.plan === 'pro'
  const isBasic = subscription?.plan === 'basic'
  const isActive = subscription?.subscriptionStatus === 'active'
  const hasOverride = subscription?.planSource === 'admin_override'

  // Check if user can access a feature based on plan
  const canAccess = (requiredPlan) => {
    if (!subscription) return false
    const planHierarchy = { free: 0, basic: 1, pro: 2 }
    return planHierarchy[subscription.plan] >= planHierarchy[requiredPlan]
  }

  return {
    subscription,
    loading,
    error,
    // Computed properties
    isSubscribed,
    isPro,
    isBasic,
    isActive,
    hasOverride,
    // Methods
    canAccess,
  }
}

export default useSubscription
